import { useCallback } from 'react';

import { type Address, toBytes } from 'viem';
import { useAccount, useConnections, useSignMessage, useSignTypedData, useSwitchAccount } from 'wagmi';

import { wagmiConfig } from '@pkg/providers';
import { SignatureKind, type StepItem } from '@pkg/types';

export function useSignature() {
    const { chainId } = useAccount();
    const { signTypedDataAsync } = useSignTypedData();
    const { signMessageAsync } = useSignMessage();
    const { switchAccountAsync } = useSwitchAccount({ config: wagmiConfig });

    const connections = useConnections();

    return useCallback(
        async (currentItem: StepItem, account: Address) => {
            const signatureData = currentItem?.data.sign;
            const { signatureKind, domain, types, primaryType, value, message: resMessage } = signatureData || {};
            // todo - dedup & switch in useWalletConnections maybe
            if (domain?.chainId && chainId && chainId !== domain?.chainId) {
                const connector = connections.find((connection) => {
                    const connectionChain = connection.chainId;
                    return connectionChain == domain?.chainId;
                })?.connector;
                if (connector) {
                    await switchAccountAsync({
                        connector,
                    });
                }
            }
            if (signatureKind === SignatureKind.EIP191 && resMessage?.length) {
                // If message is a hash, convert it to raw bytes
                const message = resMessage.match(/0x[0-9a-fA-F]{64}/) ? toBytes(resMessage) : resMessage;
                return await signMessageAsync({ message });
            }

            if (signatureKind === SignatureKind.EIP712 && domain && types && primaryType && value) {
                return await signTypedDataAsync({
                    account,
                    types,
                    primaryType,
                    message: value,
                    domain,
                });
            }
        },
        [chainId, connections, signMessageAsync, signTypedDataAsync, switchAccountAsync],
    );
}
